import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import moment from "moment";

import UserSideBarMenu from "../Component/UserSidebarMenu";
import TopHeader from "../Component/TopHeader";
import ExecutePayment from "../Component/ExecutePayment";
import AcceptBookingModal from "../Component/Models/AcceptBookingModal";
import KebabMenu from "../Component/KebabMenu";
import Pagination from "../Component/Pagination";
import ConfirmModal from "../Component/Models/ConfirmModal";

import { API } from "../../utils/api";
import { env as appEnv } from "../../utils/env";
import {
  display,
  isBookingArrivalPassed,
  showToast,
} from "../../utils/helper";
import { trackEvent } from "../../utils/analytics";
import usePaymentRedirectReturn from "../../utils/usePaymentRedirectReturn";
import {
  acceptBookingRequestAction,
  acceptBookingTimechangeAction,
  denyBookingRequestAction,
  denyBookingTimechangeAction,
  getHostRequestsAction,
} from "../../Store/Actions/BookingActions";
import { setStripeKey } from "../../Store/Reducers/AuthReducers";

const REQUEST_TYPE_LABELS = {
  booking: "Booking",
  timeChange: "Time change",
  payment: "Payment due",
};

const formatRequestStart = (item) => {
  const date = item.oldStartDate || item.startDate;
  const time = item.oldStartTime || item.startTime;
  if (!date || !time) return "";
  const m = moment(`${date} ${time}`, [
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD hh:mm A",
  ]);
  return m.isValid() ? m.format("MM/DD/YY hh:mm A") : `${date} ${time}`;
};

const RequestsList = () => {
  const dispatch = useDispatch();
  const menuState = useSelector((state) => state.auth.isMenuOpen);
  const hostRequests = useSelector((state) => state.bookings.hostRequests);

  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [acceptModal, setAcceptModal] = useState({ show: false, item: null });
  const [denyModal, setDenyModal] = useState({ show: false, item: null });
  const [pagging, setPagging] = useState({
    totalItems: "",
    itemsPerPage: "",
    currentPage: "",
    totalPages: "",
    nextPage: 1,
    limit: 15,
  });
  const [paymentModal, setPaymentModal] = useState({
    show: false,
    paymentInfo: null,
    bookingInfo: null,
  });

  const loadRequests = (page = 1) => {
    if (loading) return;
    setLoading(true);
    getHostRequestsAction({ page, limit: pagging.limit }, dispatch)
      .then((res) => {
        if (res && res.type === "success") {
          setPagging({
            ...pagging,
            ...res.pagination,
          });
        }
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not load requests"),
      )
      .finally(() => setLoading(false));
  };

  const fetchStripeKey = () => {
    const keyType = appEnv === "prod" ? "live" : "test";
    API.getPublicKeys({ type: keyType })
      .then((res) => {
        if (
          res &&
          res.type === "success" &&
          res.data &&
          res.data.stripePublicKey
        ) {
          dispatch(setStripeKey({ key: res.data.stripePublicKey }));
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadRequests();
    fetchStripeKey();
  }, []);

  usePaymentRedirectReturn(() => loadRequests());

  const runAccept = (item, grossAmount) => {
    if (busyId) return;
    setBusyId(item.id);

    let action;
    if (item.requestType === "timeChange") {
      action = acceptBookingTimechangeAction(
        { timeChangeId: item.id },
        dispatch,
      );
    } else {
      const payload = { id: item.id, bookingId: item.id };
      if (grossAmount != null) payload.grossAmount = grossAmount;
      action = acceptBookingRequestAction(payload, dispatch);
    }

    action
      .then((res) => {
        if (res && res.type === "success") {
          trackEvent("web_booking_accepted", {
            booking_id: item.id,
            request_type: item.requestType || "booking",
          });
          showToast("success", res.message || "Request accepted");
          setAcceptModal({ show: false, item: null });
        } else {
          showToast("error", (res && res.message) || "Could not accept");
        }
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not accept"),
      )
      .finally(() => setBusyId(null));
  };

  const handleAccept = (item) => {
    if (busyId) return;
    // Custom bookings let the host edit the final price before accepting.
    if (item.requestType === "booking" && item.type === "custom") {
      setAcceptModal({ show: true, item });
    } else {
      runAccept(item, null);
    }
  };

  const handleDeny = (item) => {
    if (busyId) return;
    setDenyModal({ show: true, item });
  };

  const performDeny = () => {
    const item = denyModal.item;
    if (!item || busyId) return;
    setBusyId(item.id);

    const action =
      item.requestType === "timeChange"
        ? denyBookingTimechangeAction({ timeChangeId: item.id }, dispatch)
        : denyBookingRequestAction(
            { id: item.id, bookingId: item.id },
            dispatch,
          );

    action
      .then((res) => {
        if (res && res.type === "success") {
          trackEvent("web_booking_denied", {
            booking_id: item.id,
            request_type: item.requestType || "booking",
          });
          showToast("success", res.message || "Request denied");
        } else {
          showToast("error", (res && res.message) || "Could not deny");
        }
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not deny"),
      )
      .finally(() => {
        setBusyId(null);
        setDenyModal({ show: false, item: null });
      });
  };

  const handlePay = (item) => {
    if (busyId) return;
    const paymentId = item.payment && item.payment.id;
    if (!paymentId) {
      showToast("error", "Payment record missing for this request");
      return;
    }
    setBusyId(item.id);
    API.refreshPaymentIntent({ bookingId: item.id, paymentId })
      .then((res) => {
        const payment = res && res.data && res.data.payment;
        if (
          res &&
          res.type === "success" &&
          payment &&
          payment.clientSecret
        ) {
          setPaymentModal({
            show: true,
            bookingInfo: (res.data && res.data.booking) || item,
            paymentInfo: {
              ...payment,
              paymentId: payment.paymentId || paymentId,
              grossAmount:
                payment.grossAmount || item.grossAmount || "0",
              stripeFee: payment.stripeFee || 0,
              amount: payment.amount || item.grossAmount || "0",
            },
          });
        } else {
          showToast(
            "error",
            (res && res.message) || "Could not start payment",
          );
        }
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not start payment"),
      )
      .finally(() => setBusyId(null));
  };

  const renderPagging = () => (
    <Pagination
      currentPage={pagging.currentPage || 1}
      totalPages={pagging.totalPages || 1}
      totalItems={pagging.totalItems}
      onPageChange={(p) => loadRequests(p)}
    />
  );

  return (
    <div className={`app-wrapper ${menuState ? "open" : ""}`}>
      <UserSideBarMenu />

      <div className="content-wrapper">
        <TopHeader title={"Requests"} />

        <div className="content">
              <div className="data-listing">
                <table className="table">
                  <thead className="thead-light">
                    <tr>
                      <th width="14%">Spot</th>
                      <th width="22%">Address</th>
                      <th width="10%">Type</th>
                      <th width="13%">Start</th>
                      <th width="8%" className="text-center">
                        Amount
                      </th>
                      <th width="14%" className="text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {hostRequests.length > 0 &&
                      hostRequests.map((item) => {
                        const spot = item.spot || {};
                        const requestType = item.requestType || "booking";
                        const typeLabel =
                          REQUEST_TYPE_LABELS[requestType] || requestType;
                        const isBusy = busyId === item.id;
                        // Once the arrival time is in the past it's too
                        // late for any host action — accept/deny/pay all
                        // disappear and we show a muted "Expired" label.
                        const expired = isBookingArrivalPassed(item);

                        return (
                          <tr key={`request-${item.id}`}>
                            <td>{display(spot.name)}</td>
                            <td>{display(spot.address)}</td>
                            <td>{typeLabel}</td>
                            <td>{formatRequestStart(item)}</td>
                            <td className="text-center">
                              {item.grossAmount ? `$${item.grossAmount}` : "—"}
                            </td>
                            <td className="text-center">
                              {expired ? (
                                <span className="text-muted small">
                                  Expired
                                </span>
                              ) : requestType === "payment" ? (
                                <Button
                                  size="sm"
                                  variant=""
                                  className="list-add-btn"
                                  disabled={isBusy}
                                  onClick={() => handlePay(item)}
                                >
                                  <span>
                                    {isBusy && (
                                      <i className="fas fa-spin fa-spinner me-1" />
                                    )}
                                    Pay Now
                                  </span>
                                </Button>
                              ) : (
                                <KebabMenu
                                  items={[
                                    {
                                      label: isBusy ? "Accepting…" : "Accept",
                                      icon: "fas fa-check",
                                      disabled: isBusy,
                                      onClick: () => handleAccept(item),
                                    },
                                    {
                                      label: "Deny",
                                      icon: "fas fa-times",
                                      danger: true,
                                      disabled: isBusy,
                                      onClick: () => handleDeny(item),
                                    },
                                  ]}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}

                    {hostRequests.length === 0 && !loading && (
                      <tr>
                        <td colSpan="7">
                          <p className="text-center mb-0">
                            No pending requests.
                          </p>
                        </td>
                      </tr>
                    )}

                    {loading && (
                      <tr>
                        <td colSpan="7">
                          <p className="text-center mb-0">
                            <i className="fas fa-spinner fa-spin" />
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagging()}
        </div>
      </div>

      {acceptModal.show && acceptModal.item && (
        <AcceptBookingModal
          show={acceptModal.show}
          request={acceptModal.item}
          saving={busyId === acceptModal.item.id}
          onClose={() => setAcceptModal({ show: false, item: null })}
          onConfirm={(grossAmount) => runAccept(acceptModal.item, grossAmount)}
        />
      )}

      {paymentModal.show && paymentModal.paymentInfo && (
        <ExecutePayment
          show={paymentModal.show}
          bookingInfo={paymentModal.bookingInfo}
          paymentInfo={paymentModal.paymentInfo}
          onClose={() =>
            setPaymentModal({
              show: false,
              paymentInfo: null,
              bookingInfo: null,
            })
          }
          resetForm={() => {}}
          onSuccess={() => {
            setPaymentModal({
              show: false,
              paymentInfo: null,
              bookingInfo: null,
            });
            loadRequests();
          }}
        />
      )}

      <ConfirmModal
        show={denyModal.show}
        title="Deny Request"
        message={
          denyModal.item && denyModal.item.spot && denyModal.item.spot.name
            ? `Deny this request for "${denyModal.item.spot.name}"?`
            : "Are you sure want to deny this request?"
        }
        confirmLabel="Yes, deny"
        cancelLabel="No"
        busy={denyModal.item ? busyId === denyModal.item.id : false}
        onClose={() => setDenyModal({ show: false, item: null })}
        onConfirm={performDeny}
      />
    </div>
  );
};

export default RequestsList;
